import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import { updateStandardOnboardingStep } from '../api/standardOnboarding';
import { fetchActiveChannelStatus } from '../api/channels';
import '../styles/pages/standard-onboarding.css';

/* ============================================
   Icons (local to this page)
   ============================================ */
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
);
const DashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /></svg>
);
const SpinnerIcon = () => (
  <svg className="step-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M12 2a10 10 0 0 1 10 10" />
  </svg>
);

/* ============================================
   Skeleton placeholder while data loads
   ============================================ */
const StandardOnboardingSkeleton = () => (
  <div className="onboarding-skeleton">
    <div className="skeleton-progress-card">
      <div className="skeleton-row">
        <div className="skeleton-text skeleton-text--sm" />
        <div className="skeleton-text skeleton-text--xs" />
      </div>
      <div className="skeleton-bar" />
      <div className="skeleton-text skeleton-text--md" />
    </div>
    <div className="skeleton-steps">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="skeleton-step">
          <div className="skeleton-circle" />
          <div className="skeleton-step-content">
            <div className="skeleton-text skeleton-text--lg" />
            <div className="skeleton-text skeleton-text--md" />
          </div>
          <div className="skeleton-text skeleton-text--btn" />
        </div>
      ))}
    </div>
  </div>
);

/* ============================================
   Verification overlay (shown on step 6 action)
   Phases:
     'loading'      — backend check in flight
     'success'      — widget script confirmed installed → activation done
     'not_detected' — script hasn't pinged us yet; user can retry or skip
   ============================================ */
const VerificationOverlay = ({ phase, t, onRetry, onSkip, onClose, retrying }) => (
  <div className="verification-overlay">
    <div className="verification-overlay__backdrop" onClick={phase === 'not_detected' ? onClose : undefined} />
    <div className="verification-overlay__modal">
      {phase === 'loading' && (
        <>
          <div className="verification-overlay__spinner" />
          <h3 className="verification-overlay__title">{t('standard.verifying')}</h3>
          <p className="verification-overlay__text">{t('standard.verifyingDesc')}</p>
        </>
      )}
      {phase === 'success' && (
        <>
          <div className="verification-overlay__check">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="16 8 10 16 7 13" />
            </svg>
          </div>
          <h3 className="verification-overlay__title">{t('standard.verifySuccess')}</h3>
          <p className="verification-overlay__text">{t('standard.verifySuccessDesc')}</p>
        </>
      )}
      {phase === 'not_detected' && (
        <>
          <div className="verification-overlay__warn">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h3 className="verification-overlay__title">{t('standard.notDetected')}</h3>
          <p className="verification-overlay__text">{t('standard.notDetectedDesc')}</p>
          <div className="verification-overlay__actions">
            <button
              type="button"
              className="step-action__btn step-action__btn--outlined"
              onClick={onSkip}
              disabled={retrying}
            >
              {t('standard.skipForNow')}
            </button>
            <button
              type="button"
              className="step-action__btn step-action__btn--primary"
              onClick={onRetry}
              disabled={retrying}
            >
              {retrying ? t('standard.verifying') : t('standard.tryAgain')}
            </button>
          </div>
        </>
      )}
    </div>
  </div>
);

/* ============================================
   Step-to-route mapping
   ============================================ */
const STEP_ROUTES = {
  1: '/knowledge-base',
  2: '/settings',
  3: '/channels',
  4: '/widget',
  5: '/test-bot',
};

/* ============================================
   StandardOnboardingPage Component
   ============================================ */
const StandardOnboardingPage = ({ items, setItems, onNavigate, loading = false }) => {
  const { t } = useTranslation('onboarding');
  // Step title/description/action/capability come from the api defaults (English);
  // translate them by step id, falling back to the original text for any unmapped step.
  const stepText = (item, field) => t(`standard.steps.${item.id}.${field}`, { defaultValue: item[field] || '' });
  const [loadingSteps, setLoadingSteps] = useState({});
  const [verificationPhase, setVerificationPhase] = useState(null);
  const [hasCelebrated, setHasCelebrated] = useState(false);

  const completed = items.filter((i) => i.status === 'completed').length;
  const total = items.length;
  const requiredItems = items.filter((i) => i.type === 'required');
  const requiredRemaining = requiredItems.filter((i) => i.status !== 'completed').length;
  const allRequiredDone = requiredRemaining === 0;
  const progressCount = items.filter((i) => i.status === 'completed' || i.status === 'skipped').length;

  const fireConfetti = useCallback(() => {
    const fire = () => {
      if (window.confetti) {
        const defaults = { origin: { y: 0.7 }, zIndex: 10001 };
        window.confetti({ ...defaults, angle: 60, spread: 55, particleCount: 80 });
        window.confetti({ ...defaults, angle: 90, spread: 100, particleCount: 100 });
        window.confetti({ ...defaults, angle: 120, spread: 55, particleCount: 80 });
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
      script.onload = () => {
        if (window.confetti) {
          const defaults = { origin: { y: 0.7 }, zIndex: 10001 };
          window.confetti({ ...defaults, angle: 60, spread: 55, particleCount: 80 });
          window.confetti({ ...defaults, angle: 90, spread: 100, particleCount: 100 });
          window.confetti({ ...defaults, angle: 120, spread: 55, particleCount: 80 });
        }
      };
      document.head.appendChild(script);
    };
    fire();
  }, []);

  useEffect(() => {
    if (total > 0 && progressCount === total && !hasCelebrated) {
      setHasCelebrated(true);
      fireConfetti();
    }
  }, [progressCount, total, hasCelebrated, fireConfetti]);

  const handleSkip = (id) => {
    setItems(items.map((item) => (item.id === id ? { ...item, status: 'skipped' } : item)));
    updateStandardOnboardingStep(id, 'skipped');
  };

  const handleVisitStep = (id) => {
    setItems(items.map((item) => (item.id === id ? { ...item, status: 'visited' } : item)));
    updateStandardOnboardingStep(id, 'visited').catch(() => {});
    const route = STEP_ROUTES[id];
    if (route && onNavigate) onNavigate(route.slice(1));
  };

  const handleMarkAsDone = (id) => {
    setItems(items.map((item) => (item.id === id ? { ...item, status: 'completed' } : item)));
    updateStandardOnboardingStep(id, 'completed').catch(() => {});
  };

  // Tracks whether a re-check is in flight inside the not_detected overlay
  const [verifyRetrying, setVerifyRetrying] = useState(false);

  /**
   * Verify Now → confirm at least one channel is actually live before marking
   * activation complete. Calls ``GET /bot/channels/active-status`` which reads
   * each per-channel table directly (not the legacy social_channels aggregate):
   *
   *   - webchat:   script_installed_at IS NOT NULL (widget beacon fired)
   *   - whatsapp / instagram / facebook / email: status = 'active'
   *   - telegram:  row exists in telegram_channels
   *
   * Outcomes:
   *   - active=true  → POST onboarding step 6 = completed, success overlay,
   *                    confetti, navigate to dashboard.
   *   - active=false → not_detected overlay; user can retry the check or
   *                    skip (skip marks the step ``skipped`` so the row
   *                    reflects an explicit user choice, not silent success).
   */
  const handleVerify = async () => {
    const channelStep = items.find((i) => i.id === 3);
    if (!channelStep || channelStep.status !== 'completed') return;

    setVerificationPhase('loading');
    const status = await fetchActiveChannelStatus().catch(() => ({ active: false }));

    if (!status.active) {
      setVerificationPhase('not_detected');
      return;
    }

    await _completeVerification();
  };

  const _completeVerification = async () => {
    setHasCelebrated(true);
    try {
      await updateStandardOnboardingStep(6, 'completed');
    } catch { /* proceed regardless — local state still reflects success */ }

    setItems(items.map((item) => (item.id === 6 ? { ...item, status: 'completed' } : item)));
    setVerificationPhase('success');
    fireConfetti();
    await new Promise((res) => setTimeout(res, 2000));
    setVerificationPhase(null);
    onNavigate('dashboard');
  };

  // Re-poll active-status from the not_detected overlay
  const handleVerifyRetry = async () => {
    setVerifyRetrying(true);
    const status = await fetchActiveChannelStatus().catch(() => ({ active: false }));
    setVerifyRetrying(false);
    if (status.active) {
      await _completeVerification();
    }
    // else: stay on the not_detected overlay so the user can connect a channel
    // (or paste the widget snippet on their site) and try again.
  };

  const handleVerifySkip = async () => {
    setVerificationPhase(null);
    setItems(items.map((item) => (item.id === 6 ? { ...item, status: 'skipped' } : item)));
    try {
      await updateStandardOnboardingStep(6, 'skipped');
    } catch { /* swallow — UI already reflects skip */ }
  };

  const handleVerifyClose = () => {
    setVerificationPhase(null);
  };

  const handleNavigate = (id) => {
    const route = STEP_ROUTES[id];
    if (route && onNavigate) onNavigate(route.slice(1));
  };

  const renderStepCircle = (item) => {
    if (loadingSteps[item.id]) return <div className="step-circle step-circle--loading"><SpinnerIcon /></div>;
    if (item.status === 'completed') return <div className="step-circle step-circle--completed"><CheckIcon /></div>;
    if (item.status === 'visited') return <div className="step-circle step-circle--visited">{item.id}</div>;
    if (item.status === 'skipped') return <div className="step-circle step-circle--skipped"><DashIcon /></div>;
    return <div className="step-circle step-circle--pending">{item.id}</div>;
  };

  const renderBadge = (item) => {
    if (item.status === 'completed' || item.status === 'skipped' || item.status === 'visited') return null;
    if (item.type === 'required') return null;
    if (item.type === 'recommended') return <span className="step-badge step-badge--recommended">{t('standard.recommended')}</span>;
    if (item.type === 'optional') return <span className="step-badge step-badge--optional">{t('standard.optional')}</span>;
    return null;
  };

  const renderAction = (item) => {
    const isLoading = !!loadingSteps[item.id];

    if (item.status === 'completed') {
      return item.action ? (
        <button className="step-action__btn step-action__btn--default" onClick={() => handleNavigate(item.id)}>{stepText(item, 'action')}</button>
      ) : null;
    }

    if (item.status === 'visited') {
      const route = STEP_ROUTES[item.id];
      return (
        <>
          {route && (
            <span className="step-action__revisit" onClick={() => handleNavigate(item.id)}>{stepText(item, 'action')}</span>
          )}
          <button
            className="step-action__btn step-action__btn--mark-done"
            onClick={() => handleMarkAsDone(item.id)}
            disabled={isLoading}
          >
            {isLoading ? t('standard.processing') : (<><CheckIcon /> {t('standard.markAsDone')}</>)}
          </button>
        </>
      );
    }

    if (item.status === 'skipped') {
      return (
        <button className="step-action__btn step-action__btn--outlined" disabled={isLoading} onClick={() => handleVisitStep(item.id)}>
          {isLoading ? t('standard.processing') : t('standard.configure')}
        </button>
      );
    }

    const isVerifyStep = item.id === 6;
    if (isVerifyStep) {
      const otherRequiredDone = items.filter((i) => i.type === 'required' && i.id !== 6).every((i) => i.status === 'completed');
      return (
        <button className="step-action__btn step-action__btn--primary" onClick={() => handleVerify()} disabled={!otherRequiredDone || isLoading}>
          {isLoading ? t('standard.processing') : t('standard.verifyNow')}
        </button>
      );
    }
    if (item.type === 'optional' || item.type === 'recommended') {
      return (
        <>
          <span className="step-action__skip" onClick={() => handleSkip(item.id)}>{t('standard.skip')}</span>
          <button className="step-action__btn step-action__btn--outlined" disabled={isLoading} onClick={() => handleVisitStep(item.id)}>
            {isLoading ? t('standard.processing') : stepText(item, 'action')}
          </button>
          <button className="step-action__btn step-action__btn--mark-done" onClick={() => handleMarkAsDone(item.id)} disabled={isLoading}>
            {isLoading ? t('standard.processing') : (<><CheckIcon /> {t('standard.markAsDone')}</>)}
          </button>
        </>
      );
    }
    return (
      <>
        <button className="step-action__btn step-action__btn--outlined" disabled={isLoading} onClick={() => handleVisitStep(item.id)}>
          {isLoading ? t('standard.processing') : stepText(item, 'action')}
        </button>
        <button className="step-action__btn step-action__btn--mark-done" onClick={() => handleMarkAsDone(item.id)} disabled={isLoading}>
          {isLoading ? t('standard.processing') : (<><CheckIcon /> {t('standard.markAsDone')}</>)}
        </button>
      </>
    );
  };

  return (
    <div id="page-standard-onboarding">
      {verificationPhase && (
        <VerificationOverlay
          phase={verificationPhase}
          t={t}
          onRetry={handleVerifyRetry}
          onSkip={handleVerifySkip}
          onClose={handleVerifyClose}
          retrying={verifyRetrying}
        />
      )}

      <div className="onboarding-container">
        <div className="onboarding-header">
          <p className="onboarding-header__welcome">{t('standard.welcome')}</p>
          <p className="onboarding-header__subtitle">{t('standard.subtitle')}</p>
          <p className="onboarding-header__tip" dangerouslySetInnerHTML={{ __html: t('standard.tip', { interpolation: { escapeValue: false } }) }} />

          {loading ? (
            <StandardOnboardingSkeleton />
          ) : (
            <>
              <div className={`progress-card ${progressCount === total ? 'progress-card--complete' : ''}`}>
                <div className="progress-card__top">
                  <span className="progress-card__label">{progressCount === total ? t('standard.setupComplete') : t('standard.setupProgress')}</span>
                  <span className="progress-card__count">{completed}/{total}</span>
                </div>
                <div className="progress-card__bar">
                  <div className="progress-card__bar-fill" style={{ width: `${total > 0 ? (progressCount / total) * 100 : 0}%` }} />
                </div>
                <p className="progress-card__status">
                  {progressCount === total
                    ? t('standard.liveAndReady')
                    : allRequiredDone
                      ? `${t('standard.readyToActivate')}`
                      : t('standard.stepsRemaining', { count: total - progressCount, plural: (total - progressCount) !== 1 ? 's' : '' })}
                </p>
              </div>

              <div className="steps-list">
                {items.map((item) => (
                  <div key={item.id} className={`step-item ${item.status === 'completed' || item.status === 'skipped' ? 'step-item--done' : ''} ${loadingSteps[item.id] ? 'step-item--loading' : ''} ${item.capabilityLine ? 'step-item--has-detail' : ''}`}>
                    {renderStepCircle(item)}
                    <div className="step-content">
                      <div className="step-content__top">
                        <h3 className="step-content__title">{stepText(item, 'title')}</h3>
                        {renderBadge(item)}
                      </div>
                      {item.description && <p className="step-content__description">{stepText(item, 'description')}</p>}
                      {item.capabilityLine && item.status !== 'completed' && item.status !== 'skipped' && (
                        <div className="step-capabilities">
                          <span className="step-capabilities__text">{stepText(item, 'capabilityLine')}</span>
                        </div>
                      )}
                    </div>
                    <div className="step-action">{renderAction(item)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Help Section — hidden until help resources are ready */}
        {false && (
        <div className="help-card">
          <h3 className="help-card__title">{t('helpCard.title')}</h3>
          <p className="help-card__description">{t('helpCard.description')}</p>
          <div className="help-card__actions">
            <button className="help-card__btn help-card__btn--primary">{t('helpCard.watchVideos')}</button>
            <button className="help-card__btn help-card__btn--secondary">{t('helpCard.helpCenter')}</button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default StandardOnboardingPage;
